import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// --- IMPORTS ---
import Layout from "../../../../Layout/Layout";
import CustomerConstants from "../../../../common/CustomerConstants";
// eslint-disable-next-line
import Loader from "../../../../Components/Loader";
import { SearchField } from "../../../../Components/formElements/SearchField";
import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import { formatDateWithAMPM } from "../../../../common/Validations";
import {
  getEquipmentRentFormList,
  setEquipmentRentDocumentNumber,
} from "../../../../redux/feature/customer_app/customerSlice";
import MultiColumnFilter from "../../../../Components/MultiColumnFilter/MultiColumnFilter";
import CancelModal from "./ERFCancelModel";
import ShowStatus from "../../../../Components/Modal/supplier_app/Workflow/ShowStatus";
import CommonItemLevelDatatableModal from "../../../../Components/Modal/supplier_app/CommonItemLevelDatatableModal";
import { useReactToPrint } from "react-to-print";
import ErfPrintout from "./ErfPrintout";
import DispatchDetailsModal from "./DispatchDetailsModal";
import toast from "react-hot-toast";
import EarlyClosureFormPrintout from "./EarlyClosureFormPrintout";
import ClosureRefundFormPrintout from "./ClosureRefundFormPrintout";
import DeliveryChallanPrintout from "./DeliveryChallanPrintout";
import ReceiptPrintOut from "./ReceiptPrintOut";

const BOOLEAN_FILTER_KEYS = ["refundAmountIndicator", "creditNoteIndicator"];
const EXACT_MATCH_KEYS = ["pcCallStatus", "erfType", "pcdStatus"]; // exact match — no regex

const ERFListForExtension = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- 1. CONFIG & STATE ---
  localStorage.setItem("menu", "Equipment Rent");
  localStorage.setItem("submenu", "PCD ERF List");

  const { data: reduxData, pagination } =
    useSelector((state) => state?.customer?.equipmentRentForm) || {};

  // ── Redux user (same pattern as ERFList) ──────────────────────────────
  let userData = useSelector((state) => state?.user);
  // eslint-disable-next-line
  const [getUser, setUser] = useState();
  // eslint-disable-next-line
  const [getUserRole, setUserRole] = useState();

  useEffect(() => {
    const user = userData?.data?.userId || null;
    const userRole = userData?.data?.roles || null;
    setUser(user);
    setUserRole(userRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // ── Role flags (same as ERFList) ──────────────────────────────────────
  const isSalesPerson = useMemo(() => {
    return (
      userData?.data?.roles?.some((r) => r.roleId === "SALES-OFFICER") || false
    );
  }, [userData]);

  const isStorePerson = useMemo(() => {
    return userData?.data?.roles?.some((r) => r.roleId === "STORE") || false;
  }, [userData]);

  // Local State for Table UI
  // eslint-disable-next-line
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(
    CustomerConstants.ROW_REPORT_PER_PAGE[0],
  );
  // eslint-disable-next-line
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTxt, setSearchTxt] = useState("");
  const [isCardVisible, setCardVisible] = useState(true);
  const [filterShow, setFilterShow] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  /* Cancel Modal */
  const [getCancelModalStatus, setCancelModalStatus] = useState(false);
  const cancelModalHide = () => setCancelModalStatus(false);

  /* Handle selected rows */
  const [getSelectedRows, setSelectedRows] = useState([]);
  const handleRowSelected = ({ selectedRows }) => {
    const validRows = selectedRows.filter(
      (item) => item?.statusHeader !== "Cancelled",
    );
    setSelectedRows(validRows);
  };

  const [selectedRow, setSelectedRow] = useState(null);
  const [dispatchModal, setDispatchModal] = useState({ show: false, data: [] });

  // ── Restricted roles (same as ERFList) ───────────────────────────────
  // eslint-disable-next-line
  const RESTRICTED_ROLES = ["SALES-PCD", "SALES-OFFICER"];

  const normalizeFilters = (filters) =>
    filters.map((condition) => {
      const key = Object.keys(condition)[0];

      // Boolean fields → convert regex string to true / false
      if (BOOLEAN_FILTER_KEYS.includes(key)) {
        const regexStr = condition[key]?.$regex || "";
        const rawValue = regexStr.replace(/\.\*/g, "").toLowerCase();
        if (rawValue === "true" || rawValue === "pending")
          return { [key]: true };
        if (rawValue === "false" || rawValue === "done")
          return { [key]: false };
        return condition;
      }

      // Exact-match fields → strip regex wrapper, send plain string value
      if (EXACT_MATCH_KEYS.includes(key)) {
        const regexStr = condition[key]?.$regex || "";
        if (regexStr) {
          const rawValue = regexStr.replace(/\.\*/g, "").trim();
          return { [key]: rawValue };
        }
        return condition;
      }

      return condition;
    });

  //wihtout filter
  const fetchData = useCallback(
    async (page = 1, size = 100, search = "", filters = []) => {
      setLoading(true);
      try {
        // Base condition — always required
        // const filterConditions = [{ erfStatus: "Dispatched" }];
        const filterConditions = [
          {
            $or: [
              { erfStatus: "Dispatched" },
              { erfStatus: "Service", erfTypeCode: "NR" },
            ],
          },
        ];

        if (isSalesPerson) {
          filterConditions.push({
            status: { $in: ["In progress", "Approved"] },
          });
        } else if (isStorePerson) {
          filterConditions.push({
            status: { $in: ["Approved", "Dispatched"] },
          });
        }

        // UI filters layered on top
        if (filters.length > 0) {
          filterConditions.push(...filters);
        }

        const payload = {
          queryName: "PCDList",
          page,
          per_page: size,
          searchTerm: search,
          timeZone: "Asia/Calcutta",
          rowFilter: { $and: filterConditions },
        };

        await dispatch(getEquipmentRentFormList(payload));
      } catch (error) {
        console.error("Error fetching list", error);
        toast.error("Error loading records");
      } finally {
        setLoading(false);
      }
    },
    [isSalesPerson, isStorePerson, dispatch],
  );

  // useEffect(() => {
  //   fetchData(1, perPage, searchTxt, activeFilters);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [fetchData]);

  // useEffect(() => {
  //   if (pagination?.total && pagination.total > (reduxData?.length || 0)) {
  //     fetchData(1, pagination.total, searchTxt, activeFilters);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [pagination?.total]);

  useEffect(() => {
    if (!userData) return;
    fetchData(currentPage, perPage, searchTxt, activeFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, currentPage, perPage, activeFilters]);
  // ── Handlers ─────────────────────────────────────────────────────────
  const detailedPage = (row) => {
    dispatch(setEquipmentRentDocumentNumber(row.documentNumber));
    navigate(CustomerConstants.CUSTOMER_V5_MR_EXTENSION_ERF_DETAILS);
  };
  // eslint-disable-next-line
  const handleCreate = () => {
    navigate(CustomerConstants.CUSTOMER_V5_MR_CREATE_ERF);
  };

  // const handlePageChange = (page) => setCurrentPage(page);

  // const handlePerRowsChange = (newPerPage) => {
  //   setPerPage(newPerPage);
  //   setCurrentPage(1);
  // };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const showStatusInfo = (rowData) => {
    setModalShowStatus(true);
    setSendDataModal(rowData);
    setModelTitle("Status Information");
  };

  const hideModalStatus = () => setModalShowStatus(false);

  const [modalShowStatus, setModalShowStatus] = useState(false);
  const [sendDataModal, setSendDataModal] = useState("");
  const [modelTitle, setModelTitle] = useState("");

  // ── Multi-column filter (same pattern as ERFList) ─────────────────────
  const handleMultiColumnFilter = (filterResult) => {
    const filtersArray = Array.isArray(filterResult)
      ? filterResult
      : filterResult?.$and || [];

    const normalized = normalizeFilters(filtersArray);

    setActiveFilters(normalized);
    setCurrentPage(1); // ← reset to page 1
  };

  const handleFilterCancel = () => {
    setFilterShow(false);
    setActiveFilters([]); // ← clear filters
    setCurrentPage(1);
  };

  // ── Filter columns ────────────────────────────────────────────────────
  const filterColumns = [
    {
      id: "erfType",
      name: "ERF Type",
      selectorKey: "erfType",
      dropDown: true,
      staticOptions: [
        "Rent",
        "Sale & Rent",
        "Sale",
        "Service",
        "Nursing Service",
      ],
    },
    {
      id: "privilegeCase",
      name: "Privilege Case",
      selectorKey: "privilegeCase",
      dropDown: true,
      staticOptions: ["NOT APPLICABLE", "SPECIAL CASE", "DISCOUNT", "GIFT"],
    },
    {
      id: "pcdStatus",
      name: "PCD Status",
      selectorKey: "pcdStatus",
      dropDown: true,
      staticOptions: [
        "Expired",
        "Expires Today",
        "1 day left",
        "2 days left",
        "3 days left",
        "Valid",
      ],
    },
    {
      id: "approvedBy",
      name: "Approved By",
      selectorKey: "approvedBy",
    },
    {
      id: "hospitalName",
      name: "Hospital",
      selectorKey: "hospitalName",
    },
    {
      id: "salePoint",
      name: "Sale Point",
      selectorKey: "salePoint",
    },
    {
      id: "pcCallStatus",
      name: "PC Call Status",
      selectorKey: "pcCallStatus",
      dropDown: true,
      staticOptions: [
        "Extension",
        "Partial Ext",
        "Closed",
        "Follow Up",
        "Early Closure",
        "Partial Early Closure",
      ],
    },
    //----------------------------------------
    {
      id: "patientName",
      name: "Patient Name",
      selectorKey: "patientName",
    },
    {
      id: "billingAddress",
      name: "Billing Address",
      selectorKey: "billingAddress",
    },

    {
      id: "mobileNr",
      name: "Mobile Number",
      selectorKey: "mobileNr",
    },
    {
      id: "equipmentNr",
      name: "ERF Number",
      selectorKey: "equipmentNr",
    },
  ];

  // ── Print handlers (unchanged) ────────────────────────────────────────
  const contentRef = useRef(null);
  const [getLoadingAdvice, setLoadingAdvice] = useState([]);
  const handlePrint = useReactToPrint({ contentRef });
  const handlePrintWithData = (row) => {
    setLoadingAdvice(row);
    setTimeout(() => handlePrint(), 500);
  };

  const contentRef1 = useRef(null);
  const [getEarlyClosure, setEarlyClosure] = useState(null);
  const earlyClosureHandlePrint = useReactToPrint({ contentRef: contentRef1 });
  const handleEarlyClosurePrintWithData = (row) => {
    setEarlyClosure(row);
    setTimeout(() => earlyClosureHandlePrint(), 300);
  };
  useEffect(() => {
    if (getEarlyClosure) earlyClosureHandlePrint();
    // eslint-disable-next-line
  }, [getEarlyClosure]);

  const contentRef2 = useRef(null);
  const [getClosureRefundForm, setClosureRefundForm] = useState(null);
  const closureRefundFormHandlePrint = useReactToPrint({
    contentRef: contentRef2,
  });
  const handleClosureRefundFormPrintWithData = (row) => {
    setClosureRefundForm(row);
    setTimeout(() => closureRefundFormHandlePrint(), 300);
  };
  useEffect(() => {
    if (getClosureRefundForm) closureRefundFormHandlePrint();
    // eslint-disable-next-line
  }, [getClosureRefundForm]);

  const contentRef3 = useRef(null);
  const [getdeliveryChallan, setdeliveryChallan] = useState(null);
  const deliveryChallanHandlePrint = useReactToPrint({
    contentRef: contentRef3,
  });
  const handledeliveryChallanPrintWithData = (row) => {
    setdeliveryChallan(row);
    setTimeout(() => deliveryChallanHandlePrint(), 300);
  };
  useEffect(() => {
    if (getdeliveryChallan) deliveryChallanHandlePrint();
    // eslint-disable-next-line
  }, [getdeliveryChallan]);

  const contentRef4 = useRef(null);
  const [getReceipt, setReceipt] = useState(null);
  const ReceiptHandlePrint = useReactToPrint({ contentRef: contentRef4 });
  const handleReceiptPrintWithData = (row) => {
    setReceipt(row);
    setTimeout(() => ReceiptHandlePrint(), 300);
  };
  useEffect(() => {
    if (getReceipt) ReceiptHandlePrint();
    // eslint-disable-next-line
  }, [getReceipt]);

  // ── Column / role helpers (from localStorage — same as original) ──────
  const userROle = JSON.parse(localStorage.getItem("userData"));
  const hasDIspatchRole = userROle?.data?.roles?.some(
    (r) => r.roleId === "DISPATCH",
  );
  const userStore = JSON.parse(localStorage.getItem("userData"));
  const hasstoreRole = userStore?.data?.roles?.some(
    (r) => r.roleId === "STORE",
  );
  const shouldHideColumns = hasDIspatchRole || hasstoreRole;

  // ── Modal state ───────────────────────────────────────────────────────
  const [getItemLevelData, setItemLevelData] = useState([]);
  const [getItemLevelColumns, setItemLevelColumns] = useState([]);
  const [getModalTitle, setModalTitle] = useState("");
  const [itemLevelModalShow, setItemLevelModalShow] = useState(false);

  const remarkLineItemColInfo = [
    {
      name: "Remarks",
      cell: (row) => <span>{row?.remark || ""}</span>,
      width: "180px",
    },
    {
      name: "Version",
      cell: (row) => <span>{row?.version || ""}</span>,
      width: "180px",
    },
  ];

  const serviceHistoryLineItemColInfo = [
    {
      name: "Version",
      cell: (row) => <span className="fw-bold">{row?.versionLabel || ""}</span>,
      width: "80px",
    },
    {
      name: "Service Provider",
      cell: (row) => (
        <div>
          <p className="fw-bold">{row?.serviceProviderName || "N/A"}</p>
          <small>{row?.serviceProviderCode || ""}</small>
        </div>
      ),
      width: "180px",
    },
    {
      name: "Category",
      cell: (row) => <span>{row?.serviceAssignCategory || "N/A"}</span>,
      width: "150px",
    },
    {
      name: "Assigned Date",
      cell: (row) => (
        <span>
          {row?.assignDateAndTime
            ? new Date(row.assignDateAndTime).toLocaleString()
            : "N/A"}
        </span>
      ),
      width: "180px",
    },
    {
      name: "Assigned By",
      cell: (row) => <span>{row?.assignedBy || "N/A"}</span>,
      width: "130px",
    },
    {
      name: "Collect Money",
      cell: (row) => (
        <span>
          {row?.shouldCollectMoney ? (
            <span className="badge bg-success">Yes</span>
          ) : (
            <span className="badge bg-secondary">No</span>
          )}
        </span>
      ),
      width: "120px",
    },
  ];

  const remarkItemShowModal = (itemLevelData) => {
    setItemLevelData(itemLevelData);
    setItemLevelColumns(remarkLineItemColInfo);
    setModalTitle("ERF Remarks / Version");
    setItemLevelModalShow(true);
  };

  const serviceHistoryShowModal = (itemLevelData) => {
    setItemLevelData(itemLevelData);
    setItemLevelColumns(serviceHistoryLineItemColInfo);
    setModalTitle("Service History");
    setItemLevelModalShow(true);
  };

  const itemLevelHideModal = () => setItemLevelModalShow(false);

  // ── Additional columns (unchanged) ───────────────────────────────────
  const additionalColumns = [
    {
      name: "Feedback Form",
      selector: (row) => row.feedbackForm,
      sortable: true,
      width: "160px",
    },
    {
      name: "Closure Feedback Form",
      selector: (row) => row.closureFeedbackForm,
      sortable: true,
      width: "190px",
    },
    {
      name: "Closure Feedback Status",
      selector: (row) => row.closureFeedbackStatus,
      sortable: true,
      width: "200px",
    },
    { name: "Approved By", selector: (row) => row.approvedBy, width: "150px" },
    {
      name: "Approval Status",
      selector: (row) => row.approvalStatus,
      cell: (row) => <TableStatusField status={row.approvalStatus} />,
      width: "150px",
    },
    { name: "PB Time", selector: (row) => row.pbTime, width: "120px" },
    { name: "MD Status", selector: (row) => row.mdStatus, width: "140px" },
    { name: "MD Time", selector: (row) => row.mdTime, width: "120px" },
    { name: "Sale Type", selector: (row) => row.saleType, width: "130px" },
    {
      name: "Caregiver No",
      selector: (row) => row.caregiverNo,
      width: "150px",
    },
    { name: "Cost Centre", selector: (row) => row.costCentre, width: "150px" },
    { name: "PC Status", selector: (row) => row.pcCallStatus, width: "140px" },
    { name: "Order Type", selector: (row) => row.orderType, width: "140px" },
    {
      name: "Relationship",
      selector: (row) => row.relationship,
      width: "150px",
    },
    {
      name: "Challenges",
      selector: (row) => row.challenges,
      wrap: true,
      width: "220px",
    },
  ];

  // ── Columns (unchanged from original) ────────────────────────────────
  const columns = [
    {
      name: "Created Date",
      selector: (row) => row.orderDate,
      cell: (row) => (
        <div>
          <span className="fw-medium">{formatDateWithAMPM(row.createdAt)}</span>
        </div>
      ),
      sortable: true,
      width: "190px",
    },
    {
      name: "ERF No",
      selector: (row) => row.documentNumber,
      cell: (row) => (
        <button className="btnInfo" onClick={() => detailedPage(row)}>
          {row.documentNumber || row.equipmentRentNr}
        </button>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Erf Status",
      id: "erfStatus",
      selector: (row) => row.erfStatus,
      cell: (row) => <TableStatusField status={row.erfStatus} />,
      width: "150px",
    },
    {
      name: "ERF Type & Privilege Case",
      selector: (row) => row.erfType,
      cell: (row) => (
        <div>
          <span>
            <TableStatusField status={row.erfType} />
          </span>
          <p className="fw-bold mt-1">{row.privilegeCase}</p>
        </div>
      ),
      sortable: true,
      width: "210px",
    },
    //  {
    //   name: "Closing Soon",
    //   cell: (row) => {
    //     if (!row?.erfReminderDate) return <span>-</span>;
    //     const today = new Date();
    //     today.setHours(0, 0, 0, 0);
    //     const diffDays = Math.ceil(
    //       (new Date(row.erfReminderDate) - today) / (1000 * 60 * 60 * 24)
    //     );
    //     if (diffDays < 0 || diffDays > 3) return <span>-</span>; // ← 6,21,24 days → "-"
    //     return (
    //       <div>
    //         <span className="badge bg-warning text-dark d-block mb-1">
    //           {diffDays === 0
    //             ? "Closes Today"
    //             : diffDays === 1
    //             ? "1 day left"
    //             : `${diffDays} days left`}
    //         </span>
    //         <small className="text-muted">
    //           {formatDate(row.erfReminderDate)}
    //         </small>
    //       </div>
    //     );
    //   },
    //   width: "160px",
    // },
    // {
    //   name: "Closing Soon",
    //   cell: (row) => {
    //     if (!row?.erfReminderDate) return <span>-</span>;
    //     const today = new Date();
    //     today.setHours(0, 0, 0, 0);
    //     const diffDays = Math.ceil(
    //       (new Date(row.erfReminderDate) - today) / (1000 * 60 * 60 * 24)
    //     );
    //     if (diffDays < 0 || diffDays > 3) return <span>-</span>;

    //     const statusText =
    //       diffDays === 0
    //         ? "Closes Today"
    //         : diffDays === 1
    //         ? "1 day left"
    //         : diffDays === 2
    //         ? "2 days left"
    //         : "3 days left";

    //     return (
    //       <div>
    //         <TableStatusField status={statusText} />
    //         <small className="text-muted d-block mt-1">
    //           {formatDate(row.erfReminderDate)}
    //         </small>
    //       </div>
    //     );
    //   },
    //   width: "160px",
    // },
    // {
    //   name: "Closing Soon",
    //   cell: (row) => {
    //     //  Hide for sale type
    //     if (row?.erfType === "Sale") return <span>-</span>;

    //     if (!row?.erfReminderDate) return <span>-</span>;

    //     const today = new Date();
    //     today.setHours(0, 0, 0, 0);

    //     const diffDays = Math.ceil(
    //       (new Date(row.erfReminderDate) - today) / (1000 * 60 * 60 * 24)
    //     );

    //     if (diffDays < 0 || diffDays > 3) return <span>-</span>;

    //     const statusText =
    //       diffDays === 0
    //         ? "Closes Today"
    //         : diffDays === 1
    //         ? "1 day left"
    //         : diffDays === 2
    //         ? "2 days left"
    //         : "3 days left";

    //     return (
    //       <div>
    //         <TableStatusField status={statusText} />
    //         <small className="text-muted d-block mt-1">
    //           {formatDate(row.erfReminderDate)}
    //         </small>
    //       </div>
    //     );
    //   },
    //   width: "160px",
    // },
    {
      name: "PCD Status",
      cell: (row) => <TableStatusField status={row?.pcdStatus} />,
      width: "160px",
    },
    {
      name: "ERF For",
      selector: (row) => row.erfForType,
      cell: (row) => (
        <div className="d-flex flex-column">
          <p
            style={{
              color: row.erfForType === "Hospital" ? "red" : "green",
              fontWeight: "bold",
            }}
            className="p-0 m-0"
          >
            {row.erfForType}
          </p>
          <p className="p-0 m-0">{row?.hospitalName || ""}</p>
        </div>
      ),
      sortable: true,
      width: "140px",
    },
    {
      name: "ERF Start/ End Date",
      selector: (row) => row.erfStartDate,
      cell: (row) => {
        const displayStartDate = formatDateWithAMPM(row?.erfStartDate).split(
          " ",
        )[0];
        const displayEndDate = formatDateWithAMPM(row?.erfEndDate).split(
          " ",
        )[0];
        const allowedTypes = ["Rent", "Nursing", "Sale & Rent"];

        if (allowedTypes.includes(row?.erfType)) {
          return (
            <div>
              <p className="mb-1 text-muted">
                Start: <span>{displayStartDate || "--"}</span>
              </p>
              <p className="mb-0 text-muted">
                End: <span>{displayEndDate || "--"}</span>
              </p>
            </div>
          );
        }
        return <span className="text-muted">--</span>;
      },
      sortable: true,
      width: "168px",
    },
    {
      name: "PC Call Status",
      cell: (row) => (
        <>
          <TableStatusField status={row?.pcCallStatus} />
        </>
      ),
      width: "160px",
    },
    {
      name: "Patient Name",
      selector: (row) => row.patientName,
      cell: (row) => (
        <div>
          <p className="fw-bold">{row.patientName}</p>
          <p>{row.contactNumber}</p>
          <p>{row.billingAddress}</p>
        </div>
      ),
      sortable: true,
      width: "160px",
    },
    {
      name: "Sale Point",
      selector: (row) => row.salePoint,
      cell: (row) => (
        <div>
          <p>{row.salePoint}</p>
        </div>
      ),
      sortable: true,
      wrap: true,
      width: "120px",
    },
    {
      name: "Created By",
      selector: (row) => row.createdBy,
      cell: (row) => (
        <div>
          <p className="fw fw-bold">{row.createdBy}</p>
        </div>
      ),
      sortable: true,
      wrap: true,
      width: "120px",
    },
    {
      name: "Amount",
      selector: (row) => row?.netAmount || 0,
      cell: (row) => (
        <div
          className="fw fw-bold"
          style={{
            color: "blue",
            fontWeight: "bold",
          }}
        >
          {`${Number(row?.netAmount || 0).toFixed(2)} INR`}
        </div>
      ),
      // right: true,
      width: "130px",
      style: {
        justifyContent: "flex-end",
      },
    },
    {
      name: "Updated Remarks",
      cell: (row) => {
        const version = row?.version || "V0";
        const count = row?.updateRemarks?.length || 0;
        return (
          <>
            <span
              style={{ marginLeft: "12px" }}
              onClick={() => remarkItemShowModal(row?.updateRemarks || [])}
            >
              <i className="bi bi-calendar2-week iconTable"></i>
            </span>
            <span className="suppliersIcon">{`${count}/${version}`}</span>
          </>
        );
      },
      width: "200px",
    },
    {
      name: "Current Service Executive",
      cell: (row) => {
        const serviceExecutives = row?.assignedServiceExecutives || [];
        const count = serviceExecutives.length;
        const latestExec =
          count > 0 ? serviceExecutives[serviceExecutives.length - 1] : null;
        return (
          <>
            <span
              style={{
                marginLeft: "12px",
                cursor: count > 0 ? "pointer" : "not-allowed",
                opacity: count > 0 ? 1 : 0.5,
              }}
              onClick={() => {
                if (count > 0) {
                  serviceHistoryShowModal([
                    { ...latestExec, versionLabel: `V${count}` },
                  ]);
                }
              }}
            >
              <i className="bi bi-calendar2-week iconTable"></i>
            </span>
            <span className="suppliersIcon">
              {latestExec ? latestExec.serviceProviderName : "No Data"}
            </span>
          </>
        );
      },
      width: "200px",
    },
    {
      name: "Dispatch Details",
      cell: (row) => {
        const hasDispatch =
          Array.isArray(row?.dispatchDetails) && row.dispatchDetails.length > 0;
        return (
          <button
            className="btnInfo"
            disabled={!hasDispatch}
            onClick={() => {
              setSelectedRow(row);
              setDispatchModal({
                show: true,
                data: row?.dispatchDetails || [],
              });
            }}
            style={{
              opacity: hasDispatch ? 1 : 0.4,
              cursor: hasDispatch ? "pointer" : "not-allowed",
            }}
          >
            <i className="bi bi-truck me-1"></i>
            {hasDispatch ? `View (${row.dispatchDetails.length})` : "No Data"}
          </button>
        );
      },
      width: "160px",
    },

    ...(reduxData?.some(
      (row) =>
        Array.isArray(row?.workflowActionLogs) &&
        row.workflowActionLogs.length > 0,
    )
      ? [
          {
            name: "Workflow log",
            selector: (row) => row.workflowActionLogs,
            cell: (row) => {
              const hasLogs =
                Array.isArray(row?.workflowActionLogs) &&
                row.workflowActionLogs.length > 0;
              return (
                <div
                  style={{ width: "100%" }}
                  className="d-flex justify-content-center iconTable"
                >
                  <img
                    src="assets/img/newIcons/workflow.png"
                    alt="workflow"
                    style={{
                      width: "35px",
                      opacity: hasLogs ? 1 : 0.4,
                      cursor: hasLogs ? "pointer" : "not-allowed",
                    }}
                    className="mt-1"
                    onClick={
                      hasLogs
                        ? () => showStatusInfo(row.workflowActionLogs)
                        : undefined
                    }
                  />
                </div>
              );
            },
            sortable: true,
            width: "132px",
          },
        ]
      : []),
    {
      name: "Service History",
      cell: (row) => {
        const serviceExecutives = row?.assignedServiceExecutives || [];
        const count = serviceExecutives.length;
        return (
          <>
            <span
              style={{
                marginLeft: "12px",
                cursor: count > 0 ? "pointer" : "not-allowed",
                opacity: count > 0 ? 1 : 0.5,
              }}
              onClick={() => {
                if (count > 0) {
                  serviceHistoryShowModal(
                    serviceExecutives.map((item, index) => ({
                      ...item,
                      versionLabel: `V${index + 1}`,
                    })),
                  );
                }
              }}
            >
              <i className="bi bi-calendar2-week iconTable"></i>
            </span>
            <span className="suppliersIcon">{`${count} Assigned`}</span>
          </>
        );
      },
      width: "200px",
    },
    ...additionalColumns,
    ...(!shouldHideColumns
      ? [
          {
            name: "Receipt",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handleReceiptPrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "130px",
          },
          {
            name: "Agreement",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handlePrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "130px",
          },
          {
            name: "Early Closure Form",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handleEarlyClosurePrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "180px",
          },
          {
            name: "Closure Refund Form",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handleClosureRefundFormPrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "180px",
          },
          {
            name: "Delivery Challan",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handledeliveryChallanPrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "180px",
          },
        ]
      : []),
  ];

  // ── JSX ───────────────────────────────────────────────────────────────
  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <Row>
              <Col xs={12} md={8} lg={8}>
                <h2 className="mainTitle">PCD ERF List</h2>
                <p className="subText">List of active and closed rentals</p>
              </Col>
              <Col xs={12} md={3} lg={3} className="mt-3">
                <SearchField
                  name="searchKey"
                  value={searchTxt}
                  onChange={(e) => setSearchTxt(e.target.value)}
                  onClick={() =>
                    fetchData(1, perPage, searchTxt, activeFilters)
                  }
                  placeholder="Search..."
                />
              </Col>
              <Col xs={4} md={1} lg={1}>
                <span
                  className="filterIcon"
                  onClick={() => setFilterShow(!filterShow)}
                >
                  <i className="bi bi-filter"></i>
                </span>
              </Col>
            </Row>
            <hr />
            <Row>
              <Col xs={12} md={12} lg={12}>
                {filterShow && (
                  <MultiColumnFilter
                    columns={filterColumns}
                    data={reduxData}
                    onFilter={handleMultiColumnFilter}
                    onCancel={handleFilterCancel}
                    output="and"
                  />
                )}
              </Col>
            </Row>

            <div className="accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${isCardVisible ? "bi-chevron-down" : "bi-chevron-right"}`}
                        onClick={() => setCardVisible(!isCardVisible)}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">EL</span>
                    </Col>
                    <Col xs={6} md={4} lg={4}>
                      <span className="headerTitle">PCD Rental List</span>
                      <span className="subHeaderTitle">
                        View Details and Status
                      </span>
                    </Col>
                    <Col
                      xs={12}
                      md={8}
                      lg={8}
                      className="headerButtonAlign"
                    ></Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${isCardVisible ? "visible" : ""}`}
                >
                  <div className="accordion-body">
                    <Row>
                      <Col xs={12}>
                        <DataTable
                          title=""
                          columns={columns}
                          data={reduxData || []}
                          pagination
                          paginationServer
                          paginationPerPage={perPage}
                          paginationTotalRows={pagination?.total || 0}
                          paginationRowsPerPageOptions={
                            CustomerConstants.ROW_REPORT_PER_PAGE
                          }
                          onChangePage={handlePageChange}
                          onChangeRowsPerPage={handlePerRowsChange}
                          selectableRows
                          highlightOnHover
                          pointerOnHover
                          persistTableHead={false}
                          onSelectedRowsChange={handleRowSelected}
                        />
                      </Col>
                    </Row>
                    <div style={{ display: "none" }}>
                      <ErfPrintout ref={contentRef} data={getLoadingAdvice} />
                    </div>
                    <div style={{ display: "none" }}>
                      <EarlyClosureFormPrintout
                        ref={contentRef1}
                        data={getEarlyClosure}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <ClosureRefundFormPrintout
                        ref={contentRef2}
                        data={getClosureRefundForm}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <DeliveryChallanPrintout
                        ref={contentRef3}
                        data={getdeliveryChallan}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <ReceiptPrintOut
                        key={
                          getReceipt?.documentNumber || "Receipt-Printout-key"
                        }
                        ref={contentRef4}
                        data={getReceipt}
                        isExtension={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <CancelModal
        title="Cancel the ERF Form"
        show={getCancelModalStatus}
        onHide={cancelModalHide}
        selectedrow={getSelectedRows}
      />
      <ShowStatus
        show={modalShowStatus}
        onHide={hideModalStatus}
        rowdatamodal={sendDataModal}
        modeltitle={modelTitle}
      />
      <CommonItemLevelDatatableModal
        show={itemLevelModalShow}
        onHide={itemLevelHideModal}
        columns={getItemLevelColumns}
        data={getItemLevelData}
        title={getModalTitle}
      />
      <DispatchDetailsModal
        show={dispatchModal.show}
        onHide={() => setDispatchModal({ show: false, data: [] })}
        dispatchDetails={dispatchModal.data}
        erfData={selectedRow}
      />
    </main>
  );
};

export default ERFListForExtension;
